import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Tab, Tabs, TextField,
    Box, Typography, List, ListItem, ListItemAvatar,
    Avatar, ListItemText, IconButton, Button, CircularProgress,
    InputAdornment, Zoom, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { styled } from '@mui/material/styles';
import { useAuth } from "../../context/AuthContext";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '24px',
        padding: theme.spacing(1),
        background: '#fff',
        boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
        maxWidth: '600px',
        width: '90%'
    }
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.8rem',
    borderRadius: '12px',
    minWidth: 0,
    minHeight: 44,
    whiteSpace: 'nowrap',
    margin: '0 2px',
    padding: '6px 10px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '1rem',
        margin: '0 4px',
        padding: '12px 16px'
    },
    // En móvil ocultamos el icono para que las 3 etiquetas quepan sin desbordar
    '& .MuiTab-iconWrapper': {
        [theme.breakpoints.down('sm')]: {
            display: 'none'
        }
    },
    '&.Mui-selected': {
        backgroundColor: '#e0e7ff',
        color: '#4f46e5'
    }
}));

const ResultItem = styled(ListItem)(({ theme }) => ({
    borderRadius: '16px',
    transition: 'all 0.2s',
    marginBottom: '8px',
    border: '1px solid transparent',
    '&:hover': {
        backgroundColor: '#f8fafc',
        borderColor: '#e2e8f0',
        transform: 'translateY(-2px)'
    }
}));

const formatAmount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';
    if (number >= 10) return String(Math.round(number));
    if (number >= 1) return String(Math.round(number * 10) / 10).replace('.', ',');
    return String(Math.round(number * 100) / 100).replace('.', ',');
};

const getIngredientUnit = (ingredient) => ingredient?.standardUnit || 'gramos';

const getUnitAdornment = (unit) => unit === 'gramos' ? 'g' : unit;

const getDefaultIngredientQuantity = (ingredient) => {
    const unit = getIngredientUnit(ingredient);
    const gramsPerUnit = Number(ingredient?.gramsPerUnit);

    if (unit === 'gramos') {
        return Number.isFinite(gramsPerUnit) && gramsPerUnit > 0 ? String(gramsPerUnit) : '100';
    }

    return '1';
};

const getUnitInfoText = (ingredient) => {
    const unit = getIngredientUnit(ingredient);
    const gramsPerUnit = Number(ingredient?.gramsPerUnit);
    if (!Number.isFinite(gramsPerUnit) || gramsPerUnit <= 0) return null;

    if (unit === 'gramos') {
        return `Referencia: 1 unidad aprox. ${formatAmount(gramsPerUnit)}g`;
    }

    return `1 ${unit} equivale aprox. a ${formatAmount(gramsPerUnit)}g`;
};

const AddMealPopup = ({ open, onClose, onSelect }) => {
    const { authToken } = useAuth();
    const [tab, setTab] = useState(0); // 0: Recipes, 1: Ingredients, 2: Stock
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    // Ingredient Quantity Step
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [selectedStock, setSelectedStock] = useState(null);
    const [quantity, setQuantity] = useState('');

    const inputRef = useRef(null);

    useEffect(() => {
        if (open && inputRef.current && tab !== null) {
            // Focus when searching tab changes
            const timeout = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [tab, open]);

    const handleEntered = () => {
        inputRef.current?.focus();
    };

    const searchTimeout = useRef(null);

    // Search Logic
    useEffect(() => {
        if (tab === 2) {
            setLoading(true);
            clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(async () => {
                try {
                    const res = await fetch(`${import.meta.env.VITE_API_URL}/food-stock`, {
                        headers: { Authorization: `Bearer ${authToken}` }
                    });
                    const wrapper = await res.json();
                    const items = wrapper.data || [];
                    const normalized = searchTerm.trim().toLowerCase();
                    const filtered = normalized
                        ? items.filter(item => item.name?.toLowerCase().includes(normalized) || item.sourceRecipe?.name?.toLowerCase().includes(normalized))
                        : items;
                    setResults(filtered);
                } catch (e) {
                    console.error("Stock load error", e);
                    setResults([]);
                } finally {
                    setLoading(false);
                }
            }, 200);
            return () => clearTimeout(searchTimeout.current);
        }

        if (searchTerm.length < 2) {
            setResults([]);
            return;
        }

        setLoading(true);
        clearTimeout(searchTimeout.current);

        searchTimeout.current = setTimeout(async () => {
            try {
                const endpoint = tab === 0 ? 'recipes' : 'ingredients';
                const res = await fetch(`${import.meta.env.VITE_API_URL}/${endpoint}/search?name=${encodeURIComponent(searchTerm)}`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                const data = await res.json();
                setResults(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Search error", e);
            } finally {
                setLoading(false);
            }
        }, 400);

        return () => clearTimeout(searchTimeout.current);
    }, [searchTerm, tab, authToken]);

    const handleTabChange = (_, newValue) => {
        setTab(newValue);
        // Mantenemos searchTerm para que la búsqueda se reaplique en la nueva pestaña.
        // El useEffect de búsqueda depende de [searchTerm, tab], así que se redispara solo.
        // Limpiamos los pasos intermedios de cantidad, propios de la pestaña anterior.
        setSelectedIngredient(null);
        setSelectedStock(null);
    };

    const resetForm = () => {
        setSearchTerm('');
        setResults([]);
        setSelectedIngredient(null);
        setSelectedStock(null);
        setQuantity('');
    };

    const handleItemClick = (item) => {
        if (tab === 0) {
            // Recipe: Add immediately
            onSelect({ type: 'recipe', item });
            resetForm();
            onClose();
        } else if (tab === 1) {
            // Ingredient: Go to quantity step
            setSelectedIngredient(item);
            setQuantity(getDefaultIngredientQuantity(item));
        } else {
            setSelectedStock(item);
            setQuantity('1');
        }
    };

    const confirmIngredient = () => {
        const numericQuantity = Number(quantity);
        if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) return;
        onSelect({
            type: 'ingredient',
            item: selectedIngredient,
            quantity: numericQuantity,
            unit: getIngredientUnit(selectedIngredient)
        });
        resetForm();
        onClose();
    };

    const confirmStock = () => {
        const portions = Number(quantity);
        if (!selectedStock || !Number.isFinite(portions) || portions <= 0) return;
        const available = Number(selectedStock.portionsAvailable || 0);
        if (portions > available) return;
        onSelect({
            type: 'stock',
            item: selectedStock,
            portions
        });
        resetForm();
        onClose();
    };

    const renderIngredientStep = () => {
        const unit = getIngredientUnit(selectedIngredient);
        const unitInfo = getUnitInfoText(selectedIngredient);

        return (
            <Box p={2} textAlign="center">
                <Box mb={3}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        ¿Cuánto {selectedIngredient.name}?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Unidad estándar: <b>{unit}</b>
                    </Typography>
                    {unitInfo && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            {unitInfo}
                        </Typography>
                    )}
                </Box>

                <TextField
                    autoFocus
                    fullWidth
                    type="number"
                    label="Cantidad"
                    value={quantity}
                    inputProps={{ min: 0, step: unit === 'gramos' ? 1 : 0.5 }}
                    onChange={(e) => setQuantity(e.target.value)}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">{getUnitAdornment(unit)}</InputAdornment>,
                        sx: { borderRadius: '16px', fontSize: '1.2rem', fontWeight: 'bold' }
                    }}
                />

                <Box display="flex" gap={2} mt={4}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setSelectedIngredient(null)}
                        startIcon={<ArrowBackIcon />}
                        sx={{ borderRadius: '12px', height: 48 }}
                    >
                        Volver
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={confirmIngredient}
                        disabled={!quantity || Number(quantity) <= 0}
                        sx={{ borderRadius: '12px', bgcolor: '#4f46e5', height: 48 }}
                    >
                        Añadir
                    </Button>
                </Box>
            </Box>
        );
    };

    const renderStockStep = () => (
        <Box p={2} textAlign="center">
            <Box mb={3}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Usar {selectedStock.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Disponible: <b>{selectedStock.portionsAvailable} {selectedStock.portionUnit || 'raciones'}</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {selectedStock.storage === 'freezer' ? 'Congelador, sin caducidad' : selectedStock.expiresInDays != null ? `Caduca en ${selectedStock.expiresInDays} días` : 'Nevera'}
                </Typography>
            </Box>

            <TextField
                autoFocus
                fullWidth
                type="number"
                label="Raciones"
                value={quantity}
                inputProps={{ min: 0, max: selectedStock.portionsAvailable, step: 0.5 }}
                onChange={(e) => setQuantity(e.target.value)}
                InputProps={{
                    endAdornment: <InputAdornment position="end">{selectedStock.portionUnit || 'raciones'}</InputAdornment>,
                    sx: { borderRadius: '16px', fontSize: '1.2rem', fontWeight: 'bold' }
                }}
            />

            <Box display="flex" gap={2} mt={4}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setSelectedStock(null)}
                    startIcon={<ArrowBackIcon />}
                    sx={{ borderRadius: '12px', height: 48 }}
                >
                    Volver
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={confirmStock}
                    disabled={!quantity || Number(quantity) <= 0 || Number(quantity) > Number(selectedStock.portionsAvailable || 0)}
                    sx={{ borderRadius: '12px', bgcolor: '#4f46e5', height: 48 }}
                >
                    Usar
                </Button>
            </Box>
        </Box>
    );

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            TransitionComponent={Zoom}
            TransitionProps={{
                onEntered: handleEntered
            }}
        >
            {selectedIngredient ? (
                renderIngredientStep()
            ) : selectedStock ? (
                renderStockStep()
            ) : (
                <>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                        p={{ xs: 1.5, sm: 3 }}
                        pb={{ xs: 1, sm: 1 }}
                        sx={{
                            borderBottom: '1px solid #f1f5f9',
                            mb: 2,
                            background: '#fff',
                            borderRadius: '24px 24px 0 0'
                        }}
                    >
                        <Tabs
                            value={tab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons={false}
                            TabIndicatorProps={{ sx: { display: 'none' } }}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                minHeight: 44,
                                '& .MuiTabs-flexContainer': { gap: { xs: 0.5, sm: 1 } }
                            }}
                        >
                            <StyledTab value={0} icon={<RestaurantIcon fontSize="small" />} iconPosition="start" label="Recetas" />
                            <StyledTab value={1} icon={<LocalDiningIcon fontSize="small" />} iconPosition="start" label="Ingredientes" />
                            <StyledTab value={2} icon={<Inventory2Icon fontSize="small" />} iconPosition="start" label="Fondo" />
                        </Tabs>
                        <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0, bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 3, pt: 0, minHeight: '400px' }}>
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            placeholder={tab === 0 ? "Buscar receta..." : tab === 1 ? "Buscar ingrediente..." : "Buscar en nevera o congelador..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                                sx: { borderRadius: '16px', bgcolor: '#f8fafc', '& fieldset': { border: 'none' }, py: 0.5 }
                            }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, ml: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                            {tab === 2 ? 'El congelador se considera fondo sin caducidad' : 'Escribe al menos 3 caracteres para buscar'}
                        </Typography>

                        <Box mt={2}>
                            {loading ? (
                                <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
                            ) : (
                                <List>
                                    {results.map((item) => (
                                        <ResultItem key={item._id} button onClick={() => handleItemClick(item)}>
                                            <ListItemAvatar>
                                                <Box sx={{ width: 52, aspectRatio: '4 / 3', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#475569', fontWeight: 700, bgcolor: tab === 0 ? '#e0e7ff' : tab === 1 ? '#ffe4e6' : '#ecfeff' }}>
                                                    {item.mainImage ? (
                                                        <Box component="img" src={item.mainImage} alt={item.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        tab === 2 ? (item.storage === 'freezer' ? <AcUnitIcon /> : <Inventory2Icon />) : item.name[0]
                                                    )}
                                                </Box>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={<Typography fontWeight="600">{item.name}</Typography>}
                                                secondary={
                                                    (() => {
                                                        if (tab === 2) {
                                                            const source = item.macrosPerPortion || {};
                                                            return (
                                                                <Box display="flex" gap={1} alignItems="center" component="span" flexWrap="wrap">
                                                                    <Typography variant="caption" color="text.primary" fontWeight="bold">
                                                                        {item.portionsAvailable} {item.portionUnit || 'raciones'}
                                                                    </Typography>
                                                                    <Chip
                                                                        size="small"
                                                                        label={item.storage === 'freezer' ? 'Congelador' : 'Nevera'}
                                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                                    />
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {item.storage === 'freezer' ? 'sin caducidad' : item.expiresInDays != null ? `caduca en ${item.expiresInDays}d` : 'sin fecha'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        • {Math.round(source.kilocalories || 0)} kcal/ración
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        }
                                                        const isRecipe = tab === 0;
                                                        // Recipes have 'totalMacronutrients' calculated from backend
                                                        // Ingredients have direct props
                                                        const source = isRecipe
                                                            ? (item.totalMacronutrients || item.nutritionalInfo || item.macronutrients || item.macros || item)
                                                            : item;

                                                        const kcal = Math.round(source.kilocalories || source.calories || 0);
                                                        const p = Math.round(source.protein || source.proteins || 0);
                                                        const c = Math.round(source.carbs || source.carbohydrates || 0);
                                                        const f = Math.round(source.fat || source.fats || 0);

                                                        return (
                                                            <Box display="flex" gap={1} alignItems="center" component="span">
                                                                <Typography variant="caption" color="text.primary" fontWeight="bold">
                                                                    {kcal} kcal
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    • P: {p}g • C: {c}g • F: {f}g
                                                                </Typography>
                                                                {!isRecipe && <Typography variant="caption" color="text.secondary">/100g</Typography>}
                                                            </Box>
                                                        );
                                                    })()
                                                }
                                            />
                                            <IconButton size="small" sx={{ color: '#6366f1' }}>
                                                <AddCircleOutlineIcon />
                                            </IconButton>
                                        </ResultItem>
                                    ))}
                                    {searchTerm.length > 2 && results.length === 0 && (
                                        <Typography align="center" color="text.secondary" mt={4}>
                                            No se encontraron resultados
                                        </Typography>
                                    )}
                                </List>
                            )}
                        </Box>
                    </DialogContent>
                </>
            )}
        </StyledDialog>
    );
};

export default AddMealPopup;
